import React, { useState, type FC, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import { useSnackbar } from 'notistack';

import { PageMetadata } from 'components/Shared/PageMetadata';
import { Button } from 'components/Shared/Inputs';
import { FullScreenMessage } from 'components/Shared/FullScreenMessage';
import { useLazyQueryWithCacheExpiry } from 'providers/Apollo/useLazyQueryWithCacheExpiry';
import { useQueryWithCacheExpiry } from 'providers/Apollo/useQueryWithCacheExpiry';
import { ClientContactForm } from '../components/ClientContactForm/ClientContactForm';
import { FormViewContainer } from 'components/Forms/components/FormViewContainer';
import { getClientFullName } from 'utils';
import { useMutationWithExpiration } from 'providers/Apollo/useMutationWithExpiration';
import { type IContactAddress, type WorkFlowType } from 'components/CaseManagement/interfaces';
import { ACTIVITY_QUERY, IS_ACTIVITY_ACCEPTED_QUERY, UPDATE_CLIENT_CONTACT_ADDRESS } from '../queries';
import { Spinner } from 'components/Shared';
import { useHistoryState } from 'hooks/useHistoryState';
import { type AssessmentPage } from 'components/Forms/interfaces';
import { useAuth } from 'components/Auth';

import './ActivityDetails.scss';

interface ContactDetailsViewProps { }

export const ContactDetailsView: FC<ContactDetailsViewProps> = (_) => {  
    const { t, ready: translationsReady } = useTranslation(['emergencyContact', 'clients'], { useSuspense: false });
    const { enqueueSnackbar } = useSnackbar();

    const { activityId: activityIdString } = useParams<{activityId: string}>();
    const activityId = parseInt(activityIdString || '');
    const { effectiveProviderData: { providerId } = {} } = useAuth();

    const navigate = useNavigate();
    const [historyState] = useHistoryState<{ workflow?: WorkFlowType, pages?: AssessmentPage[] }>();
    const [fetchAttempted, setFetchAttempted] = useState(false);

    const [getActivityData, activityQuery] = useLazyQueryWithCacheExpiry(ACTIVITY_QUERY, {
        variables: { activityId: activityId },
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
        onCompleted: () => setFetchAttempted(true),
        expiryEvents: [
            'UPDATE_APPT_STATUS_MUTATION',
            'TERMINATE_UPDATE_STATUS_TODO_MUTATION',
            'SAVE_FIRST_APPT_DATE',
            'ACCEPT_REFERRAL',
        ],
    });
    
    const activityAcceptedQuery = useQueryWithCacheExpiry(IS_ACTIVITY_ACCEPTED_QUERY, {
        variables: { activityId, providerId: providerId! },
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
        onCompleted: (data) => {
            if (data?.isActivityAcceptedByProvider) {
                getActivityData();
            } else {
                navigate(-1);
            }
        }
    });

    const activity = activityQuery.data?.activity;
    const forms = activityQuery.data?.formsByActivity;
    const clientName = getClientFullName(activity?.client!);
    
    const [updateContactAddress, updateContactAddressMutation] = useMutationWithExpiration(UPDATE_CLIENT_CONTACT_ADDRESS, {
        onCompleted(data) {
            if (data?.updateClientEmergencyAddress?.includes("Error")) {
                enqueueSnackbar(`${t('error_updating_contact_info')}... ${data.updateClientEmergencyAddress}`, { variant: "error" });
            } else {
                getActivityData({ variables: { activityId }, fetchPolicy: 'network-only' });
                const { workflow, pages } = historyState ?? {};
                workflow === "FAA"
                ? navigate({ pathname: `/clients/${activityId}/forms/first_appointment_assessment` }, { state: { pageNumber: 1, pages }})
                : ["CloseCase", "ClinicalRecords"].includes(workflow!)
                ? navigate(-1)
                : navigate(0);
            }
        }
    });

    const loading = !translationsReady || activityAcceptedQuery.loading || activityQuery.loading;

    const onSubmitSuccess = async (payload: IContactAddress) => {
        const { clientAddressDeclined, ...addressInfo } = payload;
        return updateContactAddress({
            variables: {
                activityId,
                clientId: activity?.client?.clientId!,
                providerId: activity?.providerId!,
                clientAddressDeclined: clientAddressDeclined!,
                ...addressInfo
            }
        });
    }

    const renderPageContent = useCallback(() => {
        if (loading) {
            return <Spinner fillParent />
        } else {
            if (!!activity) {
                return (
                    <div className="activity_details_content_wrapper">
                        <FormViewContainer activityId={activityId} clientName={clientName}>
                            <Container maxWidth="md">
                                <Card className='clinical_records_paper'>
                                    <ClientContactForm title={t('clients_full_address')} activity={activity} busy={updateContactAddressMutation.loading} onSubmit={onSubmitSuccess} />
                                </Card>
                            </Container>
                        </FormViewContainer>
                    </div>
                );
            } else if (fetchAttempted && !activity) {
                return (
                    <FullScreenMessage title={t('clients:activity_not_available_message')}>
                        <Button variant="outline" component={Link} to={{pathname: '/clients'}}>
                            {t('clients:go_to_activities_list_button_label')}
                        </Button>
                    </FullScreenMessage>
                );
            }
        }
    }, [loading, activity, forms]);

    return (
        <>
            {
                translationsReady && 
                <PageMetadata 
                    pageDescription={t('clients_full_address')}
                    pageTitle={t('clients_full_address')}
                /> 
            }
            <div className="activity_details_view_container">
                { renderPageContent() }
            </div>
        </>
    );
}
